import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { fetchEventPlayerStats, fetchEventPlayLogs, fetchEventTeamsStats, fetchSingleEventInfo } from '../../Api/events/events.api'
import { fetchHighlightsBySlug } from '../../Api/Videos/videos.api'
import { isLiveGame } from '../../Utils/EventHelpers/eventHelpers'
import { useGameSSE } from '../ServerSent'
import {
  GAME_CHECK_INTERVAL_TIME,
  GAME_HIGHLIGHTS_INTERVAL_TIME,
  GAME_POLLING_INTERVAL_TIME,
  GAMES_STATS_SSE_ENABLED,
} from '../../Constants/season'
import { isDeepEqual } from '../../Utils/logicHelper'
import { getEventMetaData } from '../../Utils/EventHelpers/eventHelpers'
import type { GameDataHookReturn } from './types'
import type { EventGQL, EventResponse, PlayLog } from '../../Api/events/events.types'
import type { TeamGameStats } from '../../Api/teams/teams.types'
import type { PlayerGameData, PlayerGameStats } from '../../Api/players/players.types'
import type { SSEData } from '../ServerSent/types'
import type { FeedItem } from '../../Api/Feed/feed.types'

export const useGameData = (id: string | null): GameDataHookReturn => {
  const [gameEvent, setGameEvent] = useState<EventGQL | null>(null)
  const [teamStats, setTeamStats] = useState<TeamGameStats[]>([])
  const [players, setPlayers] = useState<PlayerGameData[]>([])
  const [playLogs, setPlayLogs] = useState<PlayLog[]>([])
  const [isEventLoading, setIsEventLoading] = useState<boolean>(true)
  const [highlights, setHighlights] = useState<FeedItem[]>([])
  const [selectedGoal, setSelectedGoal] = useState<PlayLog | null>(null)
  const [isLoadingPlayLogs, setIsLoadingPlayLogs] = useState<boolean>(true)
  const [gamePageMetaData, setGamePageMetaData] = useState<{
    title: string
    description: string
    keywords: string
  } | null>(null)

  const gameCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const liveGameIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const gameHighlightsIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (gameEvent && !gamePageMetaData) {
      setGamePageMetaData(getEventMetaData(gameEvent))
    }
  }, [gameEvent])

  useEffect(() => {
    if (id) {
      setGamePageMetaData(null)
      fetchInitialEvent(id)
    } else {
      console.error('Game ID is missing, cannot fetch data.')
      setIsEventLoading(false)
    }

    return () => {
      if (gameCheckIntervalRef.current !== null) {
        clearInterval(gameCheckIntervalRef.current)
        gameCheckIntervalRef.current = null
      }
      if (liveGameIntervalRef.current !== null) {
        clearInterval(liveGameIntervalRef.current)
        liveGameIntervalRef.current = null
      }
      if (gameHighlightsIntervalRef.current !== null) {
        clearInterval(gameHighlightsIntervalRef.current)
        gameHighlightsIntervalRef.current = null
      }
      setShouldConnect(false)
    }
  }, [id])

  // --- State Updaters ---

  const updateEventDataInState = useCallback((eventData: EventResponse) => {
    const { eventInfo, teamStats: newTeamStats, playerStats: newPlayerStats } = eventData
    if (!eventInfo) {
      setIsEventLoading(false)
      return
    }

    setGameEvent(eventInfo)

    if (newTeamStats?.length > 1) {
      updateTeamStats(newTeamStats, eventInfo)
    }

    if (newPlayerStats?.length > 0) {
      updatePlayerStats(newPlayerStats)
    }
    setIsEventLoading(false)
  }, [])

  const updateTeamStats = useCallback(
    (newTeamStats: TeamGameStats[], ev: EventGQL | null): void => {
      if (!ev) return
      const awayTeam = newTeamStats.find(
        (team) => team?.officialId === ev?.awayTeam?.officialId
      )
      const homeTeam = newTeamStats.find(
        (team) => team?.officialId === ev?.homeTeam?.officialId
      )
      if (!awayTeam || !homeTeam) return

      setTeamStats((currentTeamStats) => {
        const updatedTeamStats = [awayTeam, homeTeam]
        if (isDeepEqual(currentTeamStats, updatedTeamStats)) {
          return currentTeamStats
        }
        return updatedTeamStats
      })
    },
    []
  )

  const updatePlayerStats = useCallback((newPlayerStats: PlayerGameStats[]): void => {
    setPlayers((currentPlayers) => {
      const currentPlayersById = new Map(currentPlayers.map((p) => [p.officialId, p]))

      const convertedPlayerStats = newPlayerStats
        .filter(
          (stat): stat is PlayerGameStats & { officialId: string } =>
            typeof stat.officialId === 'string'
        )
        .map((stat) => {
          const existingPlayerFullData = currentPlayersById.get(stat.officialId)
          const baseForMerge = existingPlayerFullData
            ? { ...existingPlayerFullData }
            : {}

          return {
            ...baseForMerge,
            ...stat,
          } as PlayerGameData
        })

      if (!isDeepEqual(currentPlayers, convertedPlayerStats)) {
        console.log('Updating player stats')
        return convertedPlayerStats
      }
      return currentPlayers
    })
  }, [])

  const updatePlayLogs = useCallback((newPlayLogs: PlayLog[]): void => {
    if (newPlayLogs.length === 0) return
    setPlayLogs((currentPlayLogs) => {
      if (!isDeepEqual(currentPlayLogs, newPlayLogs)) {
        console.log('Updating play logs')
        return newPlayLogs
      }
      return currentPlayLogs
    })
  }, [])

  // --- SSE Handling ---

  const handleSSEUpdate = useCallback((sseData: SSEData): void => {
    if (!GAMES_STATS_SSE_ENABLED || !sseData?.data) return
    const { type, data: sseGameData } = sseData
    if (type !== 'eventUpdate' || typeof sseGameData === 'string') return

    console.log('useGameData handleSSEUpdate received:', type)

    const {
      scoreboard,
      teamStats: newTeamStats,
      playerStats: newPlayerStats,
      playLogs: newPlayLogs,
    } = sseGameData

    let updatedEvent = gameEvent

    if (scoreboard) {
      setGameEvent((currentGameEvent) => {
        if (!currentGameEvent || currentGameEvent.eventStatus !== 1) {
          return currentGameEvent
        }
        const mergedEvent = {
          ...currentGameEvent,
          homeScore: scoreboard.homeScore ?? currentGameEvent.homeScore,
          visitorScore: scoreboard.awayScore ?? currentGameEvent.visitorScore,
          homePeriodScores:
            scoreboard.homePeriodScores ?? currentGameEvent.homePeriodScores,
          visitorPeriodScores:
            scoreboard.awayPeriodScores ?? currentGameEvent.visitorPeriodScores,
          clockMinutes: scoreboard.minutes ?? currentGameEvent.clockMinutes,
          clockSeconds: scoreboard.seconds ?? currentGameEvent.clockSeconds,
          period: scoreboard.period ?? currentGameEvent.period,
        }
        updatedEvent = mergedEvent
        return mergedEvent
      })
    }

    if (newTeamStats?.length > 0) {
      updateTeamStats(newTeamStats, updatedEvent)
    }

    if (newPlayerStats?.length > 0) {
      updatePlayerStats(newPlayerStats)
    }

    if (newPlayLogs?.length > 0) {
      updatePlayLogs(newPlayLogs)
    }
  }, [])

  const { setShouldConnect, promptRefresh, setFetchFails } = useGameSSE(handleSSEUpdate)

  // --- Fetching Logic ---
  const fetchInitialEvent = useCallback(async (gameId: string): Promise<void> => {
    try {
      const { eventInfo } = await fetchEvent(gameId)
      if (eventInfo) {
        await Promise.all([fetchPlayLogs(eventInfo), fetchGameHighlights(eventInfo)])
        runGameCheckInterval(eventInfo)
      } else {
        console.warn('Initial fetch did not return eventInfo')
        setIsEventLoading(false)
      }
    } catch (err) {
      console.error('Error during initial event fetch:', err)
      setIsEventLoading(false)
    }
  }, [])

  const fetchEvent = useCallback(async (gameId: string): Promise<EventResponse> => {
    try {
      const [eventInfo, teamStats, playerStats] = await Promise.all([
        fetchSingleEventInfo(gameId),
        fetchEventTeamsStats(gameId),
        fetchEventPlayerStats(gameId),
      ])

      const response = { eventInfo, teamStats, playerStats }
      updateEventDataInState(response)
      return response
    } catch (err) {
      console.error('Error fetching event data:', err)
      setIsEventLoading(false)
      setFetchFails((prev) => prev + 1)
      return { eventInfo: null, teamStats: [], playerStats: [] }
    }
  }, [])

  const runGameCheckInterval = useCallback((ev: EventGQL): void => {
    if (gameCheckIntervalRef.current !== null || !ev) return

    console.log('Starting game check interval')
    gameCheckIntervalRef.current = setInterval(() => {
      try {
        const isLive = isLiveGame(ev)
        if (GAMES_STATS_SSE_ENABLED) {
          console.log(
            `SSE check: Game is ${isLive ? 'live' : 'not live'
            }. Setting SSE connect to: ${isLive}`
          )
          setShouldConnect(isLive)
          if (!isLive && liveGameIntervalRef.current) {
            console.log(
              'Game ended, clearing potential REST polling interval from game check'
            )
            clearInterval(liveGameIntervalRef.current)
            liveGameIntervalRef.current = null
          }
        } else {
          console.log(`REST check: Game is ${isLive ? 'live' : 'not live'}.`)
          if (isLive) {
            startLiveGameInterval(ev)
          } else if (liveGameIntervalRef.current) {
            console.log('Game ended, clearing polling interval')
            clearInterval(liveGameIntervalRef.current)
            liveGameIntervalRef.current = null
          }
        }
      } catch (err) {
        console.error('Error in game check interval:', err)
        setFetchFails((prev) => prev + 1)
      }
    }, GAME_CHECK_INTERVAL_TIME)
  }, [])

  const startLiveGameInterval = useCallback(async (liveEv: EventGQL) => {
    if (
      GAMES_STATS_SSE_ENABLED ||
      liveGameIntervalRef.current !== null ||
      !liveEv?.slugname
    )
      return

    liveGameIntervalRef.current = setInterval(async () => {
      console.log('Polling for live game data...')
      try {
        const eventData = await fetchEvent(liveEv.slugname)
        if (eventData?.eventInfo) {
          void fetchPlayLogs(eventData.eventInfo).catch((err) => {
            console.error('Error fetching play logs during polling:', err)
          })
          if (!isLiveGame(eventData.eventInfo)) {
            console.log('Game ended, stopping REST polling')
            if (liveGameIntervalRef.current !== null) {
              clearInterval(liveGameIntervalRef.current)
              liveGameIntervalRef.current = null
            }
          }
        }
      } catch (error) {
        console.error('Error in live game interval execution:', error)
        setFetchFails((prev) => prev + 1)
      }
    }, GAME_POLLING_INTERVAL_TIME)
  }, [])

  const fetchPlayLogs = useCallback(async (event: EventGQL): Promise<void> => {
    if (!event?.slugname) return
    try {
      const logs = await fetchEventPlayLogs(event.slugname)
      updatePlayLogs(logs || [])
      setIsLoadingPlayLogs(false)
    } catch (err) {
      console.error('Error fetching play logs:', err)
      setIsLoadingPlayLogs(false)
    }
  }, [])

  const fetchGameHighlights = useCallback(
    async (gameEv: EventGQL): Promise<(() => void) | undefined> => {
      if (!gameEv?.slugname) return
      try {
        const initialHighlights = await fetchHighlightsBySlug(gameEv.slugname)
        setHighlights(initialHighlights || [])
      } catch (error) {
        console.error('Error fetching initial highlights', error)
      }

      if (isLiveGame(gameEv)) {
        if (gameHighlightsIntervalRef.current !== null) return
        gameHighlightsIntervalRef.current = setInterval(async () => {
          console.log('Polling for highlights...')
          try {
            const currentHighlights = await fetchHighlightsBySlug(gameEv.slugname)
            setHighlights((prevHighlights) => {
              if (prevHighlights.length !== currentHighlights.length) {
                return currentHighlights || []
              }
              return prevHighlights
            })
          } catch (error) {
            console.error('Error fetching highlights interval:', error)
          }
        }, GAME_HIGHLIGHTS_INTERVAL_TIME)

        return () => {
          if (gameHighlightsIntervalRef.current !== null) {
            clearInterval(gameHighlightsIntervalRef.current)
            gameHighlightsIntervalRef.current = null
          }
        }
      }
      return undefined
    },
    []
  )

  const updateSelectedGoal = useCallback((playLog: PlayLog): void => {
    setSelectedGoal(playLog)
  }, [])

  const winProbabilities = useMemo(() => {
    if (!playLogs?.length || !gameEvent) return []
    return playLogs.filter((pl) => pl.awayTeamWinProbability !== null)
  }, [playLogs, gameEvent])

  return {
    gamePageMetaData,
    gameEvent,
    teamStats,
    players,
    playLogs,
    highlights,
    isEventLoading,
    isLoadingPlayLogs,
    selectedGoal,
    promptRefresh,
    winProbabilities,
    updateSelectedGoal,
    fetchGameHighlights,
  }
}
